import React, { useCallback, useMemo } from 'react'
import BaseModal from './BaseModal'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useDispatch } from 'react-redux'
import { setShareDataModal } from '../../store/reducers/modal.reducer'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { ShareDataDtoForm } from '../../dto/ShareDataDto'
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, FormHelperText, Stack, TextField } from '@mui/material'
import { t } from 'i18next'
import { isEmpty, uniqBy } from 'lodash'
import { LoadingButton } from '@mui/lab'
import { useHorses } from '../../hooks/useHorses'
import { DatePicker } from '@mui/x-date-pickers'
import { useAuth } from '../../hooks/useAuth'
import { hydraTransform } from '../../shared/utils/hydraTransformer'
import { toast } from 'react-toastify'
import { useThirdPartyUsers } from '../../hooks/useThirdPartyUser'
import { CreateParentChildDto } from '../../dto/CreateParentChildDto'
import dayjs from 'dayjs'
import AutocompleteHorse from '../horses/AutocompleteHorse'



function ShareDataModal({ onSuccess }: { onSuccess?: () => void }) {

    const open = useSelector<RootState>(state => state.modals.shareDataModal) as boolean
    const userId = useSelector<RootState>(state => state.user.user?.['@id']) as string
    const { horses, isLoadingHorses } = useHorses(open, userId)
    const dispatch = useDispatch()
    const { searchUserByEmail } = useAuth()
    const { saveParentChildMutation } = useThirdPartyUsers({ enableQuery: false })


    const {
        register,
        handleSubmit,
        control,
        reset,
        setError,
        setValue,
        formState: { errors },
    } = useForm<ShareDataDtoForm>({
        defaultValues: {
            parent: '',
            can_delete: false,
            can_edit: true,
            horse: null,
            child: userId,
            date_end: null,
            date_start: null
        }
    })


    const onSubmit: SubmitHandler<ShareDataDtoForm> = async (data) => {
        try {
            // let fetchedUserByEmail = await searchUserByEmail.mutateAsync(data.parent)

            // let fetchedUser = fetchedUserByEmail.data

            // if (!fetchedUser) {
            //     setError('parent', { type: "custom", message: t("form:error:notExistEmail") })
            //     return
            // }

            let payload: CreateParentChildDto = {
                child: data.child,
                can_edit: data.can_edit,
                parent_email: data.parent,
                can_delete: data.can_delete,
                ...(data.date_start ? { date_start: dayjs.utc(data.date_start).toISOString() } : {}),
                ...(data.date_end ? { date_end: dayjs.utc(data.date_end).toISOString() } : {}),
                horse: data.horse?.['@id'] ?? null

            }

            let response = await saveParentChildMutation.mutateAsync(payload)
            toast(t('rights:success'), { type: 'success' })

            handleClose()

            typeof onSuccess === 'function' && onSuccess()

        } catch (error: any) {
            let status_code = error.response.status
            if (status_code == 404) {
                setError('parent', { type: "custom", message: t("form:error:notExistEmail") })

            }
            else if (status_code == 400) {
                toast(t('error:generic'), { type: 'error' })
            } else {
                toast(t('error:generic'), { type: 'error' })

            }


        }
    }

    const handleClose = useCallback(() => {
        dispatch(setShareDataModal(false))
        reset()
    }, [])

    return (
        <BaseModal
            title={t('thirdPartyApi:shareTitle')}
            open={open}
            handleClose={handleClose}

        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack
                    direction={'column'}
                    spacing={2}
                >

                    <TextField
                        label={t('info:email')}
                        {...register('parent', {
                            required: true,
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t("form:error:email")
                            }
                        })}
                        error={!isEmpty(errors?.parent)}
                        helperText={errors?.parent?.message}

                    ></TextField>

                    <Box display={'flex'} flexDirection={'column'} >

                        <AutocompleteHorse
                            handleChangedValue={(selectedHorse) => { setValue('horse', selectedHorse?.horse || null) }}
                        />

                        <FormHelperText>{t('parentChildHorseHint')}</FormHelperText>
                    </Box>

                    <Controller
                        name="date_start"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={field.value}
                                onChange={(date) => field.onChange(date)} // Sync with react-hook-form
                                slotProps={{
                                    textField: {
                                        label: t('riders:startDate'),
                                        error: !!errors.date_start,
                                        helperText: t('thirdPartyApi:startEndDateHint'),
                                    },
                                }}
                            />
                        )}
                    />

                    <Controller
                        name="date_end"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={field.value}
                                onChange={(date) => field.onChange(date)} // Sync with react-hook-form
                                slotProps={{
                                    textField: {
                                        label: t('riders:endDate'),
                                        error: !!errors.date_end,
                                        helperText: t('thirdPartyApi:startEndDateHint'),
                                    },
                                }}
                            />
                        )}
                    />

                    <Controller
                        name="can_edit"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...field}
                                        checked={field.value} // Sync the checked state
                                    />
                                }
                                label={t('thirdPartyApi:editTrainings')}

                            />
                        )}
                    />

                    <Controller
                        name="can_delete"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...field}
                                        checked={field.value} // Sync the checked state
                                    />
                                }
                                label={t('thirdPartyApi:deleteTrainings')}
                            />
                        )}
                    />

                    <LoadingButton loading={searchUserByEmail.isLoading || saveParentChildMutation.isLoading} type='submit' variant='contained'>{t('save')}</LoadingButton>

                </Stack>

            </form>

        </BaseModal>
    )
}

export default ShareDataModal